import * as Sentry from "@sentry/react";

import { ArtifactThreadLink } from "@/components/artifacts/ArtifactThreadLink.tsx";
import { HeavyArtifactLightbox } from "@/components/artifacts/HeavyArtifactLightbox.tsx";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import type { ArtifactLibraryArtifactItem } from "@/types/artifactLibrary.ts";
import { useEffect } from "react";

/**
 * Full-screen view of a Library artifact. Renders the server-built `view`
 * payload through the shared `<ArtifactView>` (the server-rendered path) inside
 * the standard `HeavyArtifactLightbox` chrome — the same surface the thread chip
 * and omnibox biscuit use. Read-only: Library artifacts are already "done".
 *
 * Mounted only while an item is selected, so `open` is always true; closing
 * bubbles up via `onClose` to clear the selection.
 *
 * The header offers the thread the artifact came from. The Library is the one
 * place an artifact is seen with no conversation around it, and the lightbox
 * on its own gave no way to get there (BIG-9194).
 */
export const ArtifactLibraryLightbox = ({
  item,
  onClose,
  onOpenThread,
}: {
  item: ArtifactLibraryArtifactItem;
  onClose: () => void;
  /** Open the thread that holds this artifact; the host decides where that goes. */
  onOpenThread: (threadId: number) => void;
}) => {
  // Library items are heavy and carry a server-built `view` + resolved thread.
  // If any is missing we can't open a dialog — clear the selection so the click
  // doesn't leave the item stuck "selected" with no chrome and no way to close.
  // Read off the union before the effect: `view` lives only on the heavy arm,
  // so referencing it from a dependency array loses the narrowing.
  const hasView = item.isHeavy && !!item.view;
  const canRender = hasView && item.threadId !== null;
  useEffect(() => {
    if (canRender) return;
    // Reaching here means the click did nothing, from the user's side. The
    // server drops unmappable artifacts before they reach the client, so this
    // state means something upstream is wrong and nobody would otherwise hear
    // about it — report which field was missing rather than closing in silence.
    Sentry.captureMessage("Library artifact could not be opened", {
      level: "warning",
      extra: { artifactId: item.id, isHeavy: item.isHeavy, hasView, threadId: item.threadId },
    });
    onClose();
  }, [canRender, onClose, item.id, item.isHeavy, hasView, item.threadId]);

  if (!item.isHeavy || !item.view || item.threadId === null) return null;
  const threadId = item.threadId;

  return (
    <HeavyArtifactLightbox
      artifact={item}
      threadId={threadId}
      messageId={item.messageId}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      headerActions={<ArtifactThreadLink onOpen={() => onOpenThread(threadId)} />}
    >
      <ArtifactView
        view={item.view}
        threadId={item.threadId}
        messageId={item.messageId}
        artifactId={item.id}
        isReadOnly
        maximized
      />
    </HeavyArtifactLightbox>
  );
};
